import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Grid, TextField, MenuItem, Select, FormControl, InputLabel,
  CircularProgress, Divider, Typography
} from '@mui/material';
import { API } from '../../context/AuthContext';
import { useSnackbar } from 'notistack';

const DEFAULT = {
  name: '', businessName: '', rfc: '', email: '', phone: '', website: '',
  status: 'prospecto', source: 'otro', notes: '', creditDays: 0,
  address: { street: '', city: '', state: '', zip: '', country: 'México' }
};

export default function ClientFormDialog({ open, client, onClose, onSave }) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(DEFAULT);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    API.get('/auth/users').then(r => setUsers(r.data));
  }, []);

  useEffect(() => {
    if (client) setForm({ ...DEFAULT, ...client, address: { ...DEFAULT.address, ...client.address } });
    else setForm(DEFAULT);
  }, [client, open]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const setAddr = (field, val) => setForm(f => ({ ...f, address: { ...f.address, [field]: val } }));

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (client?._id) await API.put(`/clients/${client._id}`, form);
      else await API.post('/clients', form);
      enqueueSnackbar(client ? 'Cliente actualizado' : 'Cliente creado', { variant: 'success' });
      onSave();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error al guardar', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
        {client ? 'Editar cliente' : 'Nuevo cliente'}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}><Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'primary.main' }}>Información general</Typography></Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Nombre / Razón social *" value={form.name} onChange={e => set('name', e.target.value)} required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Nombre comercial" value={form.businessName} onChange={e => set('businessName', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="RFC" value={form.rfc} onChange={e => set('rfc', e.target.value.toUpperCase())} inputProps={{ maxLength: 13 }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select value={form.status} onChange={e => set('status', e.target.value)} label="Estado">
                {['prospecto','activo','inactivo','vip'].map(s => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Origen</InputLabel>
              <Select value={form.source} onChange={e => set('source', e.target.value)} label="Origen">
                {['referido','web','llamada','expo','otro'].map(s => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}><Divider /><Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'primary.main', mt: 1, display: 'block' }}>Contacto</Typography></Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Email" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Teléfono" value={form.phone} onChange={e => set('phone', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="Días de crédito" type="number" value={form.creditDays} onChange={e => set('creditDays', parseInt(e.target.value)||0)} />
          </Grid>

          <Grid item xs={12}><Divider /><Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'primary.main', mt: 1, display: 'block' }}>Dirección</Typography></Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Calle y número" value={form.address.street} onChange={e => setAddr('street', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Ciudad" value={form.address.city} onChange={e => setAddr('city', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Estado" value={form.address.state} onChange={e => setAddr('state', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="CP" value={form.address.zip} onChange={e => setAddr('zip', e.target.value)} />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Vendedor asignado</InputLabel>
              <Select value={form.seller || ''} onChange={e => set('seller', e.target.value)} label="Vendedor asignado">
                <MenuItem value="">Sin asignar</MenuItem>
                {users.map(u => <MenuItem key={u._id} value={u._id}>{u.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Notas internas" value={form.notes} onChange={e => set('notes', e.target.value)} multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading || !form.name}>
          {loading ? <CircularProgress size={20} /> : (client ? 'Guardar cambios' : 'Crear cliente')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
